import { rolesEnum } from "helpers/enums";
import {
    containsQuery,
    MAX_TOP_IN_SELECT_REQ,
    OdataFilters,
    ParamsFilters,
    queryCollection,
    queryParams,
} from "helpers/reqparams";
import chunk from "lodash/chunk";
import moment from "moment";
import { ITEM_ROOT } from "odata-query";
import { alertRequest, body, request } from "services/request";

export * from "./neuralNetwork";
export * from "./integrationClients";

const resource = "api/adminpanel";

const listQuery = { orderby: ["id desc"] };

const register = (params: {}) => request(`${resource}/register`, body(params));

const getBlockedEmails = (params: {
    preparedFilter: {};
    start: number;
    length: number;
    orderBy: any;
    filters?: ParamsFilters[] | undefined;
}) => {
    const { preparedFilter, ...rest } = params;
    const query = queryCollection(rest as unknown as OdataFilters, { filter: preparedFilter, ...listQuery });
    return request(`${resource}/getBlockedEmails${query}`, { method: "GET" }, { countInHeaders: true });
};

const requestStatistics = (params: { from: string; to: string }) => {
    if (params.from) {
        params.from = moment(params.from).utc().format();
    }
    if (params.to) {
        params.to = moment(params.to).utc().format();
    }
    return request(`api/logs/getApiRequestsStatistics${queryParams(params)}`, { method: "GET" });
};

const filterTrackerLogs = (params: {}) => alertRequest(`${resource}/filterTrackerLogs`, body(params));

const deleteBlockedEmail = (params: {}) => alertRequest(`${resource}/deleteBlockedEmail`, body(params));

const getOrganizationNames = (params: { filters: ParamsFilters }, filter = {}) => {
    const query = containsQuery(params, { filter });
    return request(`${resource}/getOrganizationNames${query}`, { method: "GET" });
};

const getUserProfileForEdit = (id: string) => request(`${resource}/getUserProfileForPatch/${id}`, { method: "GET" });

const editUser = (params: {}) => request(`${resource}/patchUser`, body(params));

const getAllUsers = (params: {
    preparedFilter: { and?: { roles?: {} } };
    start: any;
    length: any;
    orderBy: any;
    filters?: ParamsFilters;
}) => {
    let { preparedFilter, ...rest } = params;
    if (!preparedFilter.and) {
        preparedFilter = { and: {} };
    }
    if (!preparedFilter.and!.roles) {
        preparedFilter = { and: { ...preparedFilter.and, roles: { any: { [ITEM_ROOT]: { ne: rolesEnum.Driver } } } } };
    }
    const query = queryCollection(rest as unknown as OdataFilters, { filter: preparedFilter, ...listQuery });
    return request(`${resource}/getAllUsers${query}`, { method: "GET" }, { countInHeaders: true });
};

const getAllUsersForSelectList = (params: { filters: ParamsFilters }, filter = {}) => {
    const query = containsQuery(params, { filter });
    return request(`${resource}/getAllUsersForSelect${query}`, { method: "GET" });
};

const getAllCarsList = (params: { filters: ParamsFilters }, filter = {}) => {
    const carsReq = (queryParams: { filters: ParamsFilters }) => {
        const query = containsQuery(queryParams, { filter });
        return request(`${resource}/getAllCars${query}`, { method: "GET" });
    };

    const idFilter = params.filters.find((item) => item.dataIndex === "id");
    if (idFilter && Array.isArray(idFilter.value) && idFilter.value?.length > MAX_TOP_IN_SELECT_REQ) {
        const requests = chunk(idFilter.value, MAX_TOP_IN_SELECT_REQ).map((chunk) =>
            carsReq({ ...params, filters: [{ dataIndex: "id", value: chunk }] }),
        );
        return Promise.all(requests).then((responses) => [].concat(...responses));
    } else {
        return carsReq(params);
    }
};

const getCarsAndTrailers = (params: { filters: ParamsFilters }) => {
    const query = containsQuery(params);
    return request(`${resource}/getCarsAndTrailers${query}`, { method: "GET" });
};

const getAllTrackerNumbers = (params: { filters: ParamsFilters }) =>
    request(`${resource}/getTrackerNumbersList${containsQuery(params, { orderBy: ["id desc"] })}`, { method: "GET" });

const loginExternal = (params: {}) => request(`${resource}/loginExternal`, body(params));

const removeTrackerLogs = (params: {}) => request(`${resource}/removeTrackerLogs`, body(params));

const recalculateCoordinates = (params: {}) => request(`${resource}/recalculateCoordinates`, body(params));

const getNotificationQueue = (params: {}) => request(`${resource}/getNotificationQueue`, body(params));

const getCustomFieldsOrder = (organizationId: string) =>
    request(`${resource}/getCustomFieldsOrder/${organizationId}`, { method: "GET" });

const editCustomFieldsOrder = (params: {}) => alertRequest(`${resource}/editCustomFieldsOrder`, body(params));

const getDriverDeviceStatistics = (appType: string) =>
    request(`${resource}/getDriverDeviceStatistics${appType ? `?appType=${appType}` : ""}`, { method: "GET" });

const getOrganizationInitialSettings = () => request(`${resource}/getOrganizationInitialSettings`, { method: "GET" });
const applyOrganizationInitialSettings = (params: {}) =>
    alertRequest(`${resource}/applyOrganizationInitialSettings`, body(params));
const setOrganizationInitialSettings = (params: {}) =>
    request(`${resource}/setOrganizationInitialSettings`, body(params));
const generateOrganizationCode = () =>
    request(`${resource}/generateOrganizationCode`, { method: "GET" }, { textBody: true });
const getEmulatedSegments = (params: {}) =>
    request(`${resource}/getEmulatedSegments${queryParams(params)}`, { method: "GET" });
const getTrackerSegmentEmulatingLogs = (params: OdataFilters) => {
    const { preparedFilter, ...rest } = params;
    const query = queryCollection(rest, { filter: preparedFilter, ...listQuery });
    return request(`${resource}/getTrackerSegmentEmulatingLogs${query}`, { method: "GET" }, { countInHeaders: true });
};
const getPreservedTrackerLogs = (params: OdataFilters) => {
    const { preparedFilter, ...rest } = params;
    const query = queryCollection(rest, { filter: preparedFilter, ...listQuery });
    return request(`${resource}/getPreservedTrackerLogs${query}`, { method: "GET" }, { countInHeaders: true });
};
const blockOrganization = (params: {}) => request(`${resource}/blockOrganization`, body(params));
const unblockOrganization = (params: {}) => request(`${resource}/unblockOrganization`, body(params));
const syncTariffs = () => request(`${resource}/syncTariffs`, {});

export interface TariffListModel {
    id: number;
    externalId: number;
    name: string;
    type: number;
    paymentType: number;
    isDeleted: boolean;
}

const getTariffs = ({ preparedFilter, ...params }: OdataFilters) => {
    const query = queryCollection(params, { filter: { ...preparedFilter }, orderBy: "id desc" }).substr(1);
    return request(`${resource}/getTariffs?withDeleted=true&${query}`, { method: "GET" }, { countInHeaders: true });
};

const deleteTariff = (params: { id: number }) => alertRequest(`${resource}/DeleteTariff`, body(params));

const restoreTariff = (params: { id: number }) => alertRequest(`${resource}/RestoreTariff`, body(params));

const getReportAllowedUsers = (reportId: string) =>
    request(`${resource}/getReportAllowedUsers/${reportId}`, { method: "GET" });
const applyReportAllowedUsers = (params: {}) => request(`${resource}/applyReportAllowedUsers`, body(params));
const setEmptyDriverBidPointDates = (params: { organizationId: string; driverIds: string[] }) =>
    request(`${resource}/setEmptyDriverBidPointDates`, body(params), { withoutMiddleware: true }).then((res) =>
        res.json().catch((err: any) => {
            if (err instanceof SyntaxError) {
                return null;
            }
            throw err;
        }),
    );

export {
    register,
    getOrganizationNames,
    getAllUsers,
    loginExternal,
    removeTrackerLogs,
    recalculateCoordinates,
    getNotificationQueue,
    getUserProfileForEdit,
    editUser,
    getAllUsersForSelectList,
    getAllCarsList,
    getBlockedEmails,
    deleteBlockedEmail,
    getCarsAndTrailers,
    getCustomFieldsOrder,
    editCustomFieldsOrder,
    getAllTrackerNumbers,
    requestStatistics,
    getDriverDeviceStatistics,
    getOrganizationInitialSettings,
    generateOrganizationCode,
    applyOrganizationInitialSettings,
    setOrganizationInitialSettings,
    setEmptyDriverBidPointDates,
    filterTrackerLogs,
    getEmulatedSegments,
    getTrackerSegmentEmulatingLogs,
    getPreservedTrackerLogs,
    blockOrganization,
    unblockOrganization,
    syncTariffs,
    getTariffs,
    deleteTariff,
    restoreTariff,
    getReportAllowedUsers,
    applyReportAllowedUsers,
};
